import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../hooks/useUser";
import { Save, LogOut } from "lucide-react";
import { useToast } from "../../admin/components/Toast";

// Password is never fetched from the backend. It is only set by the user for updating.
const AccountPage = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { getMe, updateMe, loading } = useUser();
  const { addToast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    getMe(token)
      .then((data) => {
        setUser(data);
        setEmail(data.email || "");
      })
      .catch((err) => {
        addToast("Failed to fetch user info", "error");
        localStorage.removeItem("token");
        navigate("/login");
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const payload = {
        current_password: currentPassword,
      };
      if (email !== user.email) payload.new_email = email;
      if (newPassword) payload.new_password = newPassword;
      try {
        await updateMe(token, payload);
      } catch (error) {
        setMessage(
          error?.response?.data?.detail ||
            error.message ||
            "Failed to update account."
        );
        return;
      }
      setMessage("Account updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      setMessage(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to update account."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-8 px-4 text-[15px] text-gray-800 font-sans">
      <h1 className="text-xl font-bold mb-4">account</h1>
      <div className="mb-6">
        <span className="font-semibold">email:</span> {user?.email}
      </div>
      {user && (user?.city || user?.region || user?.country) && (
        <div className="mb-6">
          <span className="font-semibold">location:</span>
          {user?.city && <span> {user?.city},</span>}
          {user?.region && <span> {user?.region},</span>}
          {user?.country && <span> {user?.country}</span>}
        </div>
      )}
      <ul className="mb-8 space-y-2">
        <li>
          <a
            href="/post"
            className="text-blue-700 underline hover:text-blue-900"
          >
            create a posting
          </a>
        </li>
        <li>
          <a
            href="/my-posts"
            className="text-blue-700 underline hover:text-blue-900"
          >
            my posts
          </a>
        </li>
        {user?.is_admin && (
          <li>
            <a
              href="/admin"
              className="text-blue-700 underline hover:text-blue-900"
            >
              Admin Page
            </a>
          </li>
        )}
        {!user?.is_verified && (
          <li>
            <a
              href="/verify-email"
              className="text-blue-700 underline hover:text-blue-900"
            >
              Verify account
            </a>
          </li>
        )}
      </ul>
      <form onSubmit={handleSave} className="mb-8 space-y-4">
        <div>
          <label htmlFor="email" className="block mb-1 font-medium">
            email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-2 py-1 border border-gray-300"
            required
          />
        </div>
        <div>
          <label htmlFor="newPassword" className="block mb-1 font-medium">
            new password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 pr-8"
              placeholder="leave blank to keep current password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95m3.249-2.568A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-4.043 5.306M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6.364 6.364L19.07 4.93"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="currentPassword" className="block mb-1 font-medium">
            current password <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              id="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 pr-8"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword((v) => !v)}
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showCurrentPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95m3.249-2.568A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-4.043 5.306M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6.364 6.364L19.07 4.93"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-1 border border-gray-400 bg-gray-100 hover:bg-gray-200"
        >
          <Save className="h-4 w-4" /> save
        </button>
        {message && <div className="text-sm mt-2 text-blue-700">{message}</div>}
      </form>
      <button
        onClick={handleLogout}
        className="flex items-center gap-2 text-blue-700 underline hover:text-blue-900 mt-2"
      >
        <LogOut className="h-4 w-4" /> log out
      </button>
    </div>
  );
};

export default AccountPage;
